import { Utils } from '@pacem/pacem-core';
import { VERTEX_OUTPUT_LOCATION_NORMAL, VERTEX_OUTPUT_LOCATION_VERTEX, VERTEX_OUTPUT_LOCATION_UV, VERTEX_OUTPUT_LOCATION_INDEX, VERTEX_OUTPUT_LOCATION_RAY, VERTEX_OUTPUT_LOCATION_INSTANCE, VERTEX_BUFFER_INDEX_MESH_POSITION, VERTEX_BUFFER_INDEX_MESH_NORMAL, VERTEX_BUFFER_INDEX_MESH_UV, UNIFORM_BINDING_INDEX_CAMERA, UNIFORM_GROUP_INDEX_CAMERA, UNIFORM_BINDING_INDEX_TRANSFORM, UNIFORM_GROUP_INDEX_TRANSFORM, UNIFORM_BINDING_INDEX_INTERACTION, UNIFORM_GROUP_INDEX_INTERACTION, WGSL_VERTEXINPUT_STRUCT_NAME, WGSL_VERTEXOUTPUT_STRUCT_NAME, WGSL_INTERACTIONDATA_STRUCT_NAME } from '../constants';
import { extractMeshVertexModifierFunctionComponents } from './shared';
const DefaultMeshVertexOptions = {
    outputNormalLocation: VERTEX_OUTPUT_LOCATION_NORMAL,
    outputVertexLocation: VERTEX_OUTPUT_LOCATION_VERTEX,
    outputUvLocation: VERTEX_OUTPUT_LOCATION_UV,
    outputVertexIndexLocation: VERTEX_OUTPUT_LOCATION_INDEX,
    outputInstanceIndexLocation: VERTEX_OUTPUT_LOCATION_INSTANCE,
    outputRayLocation: VERTEX_OUTPUT_LOCATION_RAY,
    inputPositionLocation: VERTEX_BUFFER_INDEX_MESH_POSITION,
    inputNormalLocation: VERTEX_BUFFER_INDEX_MESH_NORMAL,
    inputUvLocation: VERTEX_BUFFER_INDEX_MESH_UV,
    uniformViewBindingIndex: UNIFORM_BINDING_INDEX_CAMERA,
    uniformViewGroupIndex: UNIFORM_GROUP_INDEX_CAMERA,
    uniformWorldBindingIndex: UNIFORM_BINDING_INDEX_TRANSFORM,
    uniformWorldGroupIndex: UNIFORM_GROUP_INDEX_TRANSFORM,
    uniformInteractionBindingIndex: UNIFORM_BINDING_INDEX_INTERACTION,
    uniformInteractionGroupIndex: UNIFORM_GROUP_INDEX_INTERACTION,
};
export function getMeshVertexWgsl(options = DefaultMeshVertexOptions) {
    if (options != DefaultMeshVertexOptions) {
        options = Utils.extend({}, DefaultMeshVertexOptions, options);
    }
    // vertex input modifier?
    const modifierInput = options.inputModifier;
    let modifyInstructionInput = 'vertexInput', modifierFnInput = '';
    if (!Utils.isNullOrEmpty(modifierInput)) {
        const { name: modifierName, args: [arg0, arg1], body: modifierBody } = extractMeshVertexModifierFunctionComponents(modifierInput);
        modifyInstructionInput = `${modifierName}(vertexInput, interaction)`;
        modifierFnInput = `fn ${modifierName} (${arg0}: ${WGSL_VERTEXINPUT_STRUCT_NAME}, ${arg1}: ${WGSL_INTERACTIONDATA_STRUCT_NAME}) -> ${WGSL_VERTEXINPUT_STRUCT_NAME} {
    ${modifierBody}
}`;
    }
    // vertex output modifier?
    const modifierOutput = options.outputModifier;
    let modifyInstructionOutput = 'output', modifierFnOutput = '';
    if (!Utils.isNullOrEmpty(modifierOutput)) {
        const { name: modifierName, args: [arg0, arg1], body: modifierBody } = extractMeshVertexModifierFunctionComponents(modifierOutput);
        modifyInstructionOutput = `${modifierName}(output, interaction)`;
        modifierFnOutput = `fn ${modifierName} (${arg0}: ${WGSL_VERTEXINPUT_STRUCT_NAME}, ${arg1}: ${WGSL_INTERACTIONDATA_STRUCT_NAME}) -> ${WGSL_VERTEXOUTPUT_STRUCT_NAME} {
    ${modifierBody}
}`;
    }
    const opts = Utils.extend({}, DefaultMeshVertexOptions, options);
    return `struct ${WGSL_VERTEXOUTPUT_STRUCT_NAME} {
    @builtin(position) position : vec4f,                                                // clip space position
    @location(${opts.outputNormalLocation}) normal: vec3f,                              // world space normal
    @location(${opts.outputVertexLocation}) vertex : vec3f,                             // world space position
    @location(${opts.outputUvLocation}) uv : vec2f,                                     // texture coordinates
    @location(${opts.outputVertexIndexLocation}) @interpolate(flat) index : u32,        // vertex index
    @location(${opts.outputRayLocation}) ray : vec3f,                                   // eye-to-point vector in world space
    @location(${opts.outputInstanceIndexLocation}) @interpolate(flat) instance : u32,   // instance index
}

struct ${WGSL_VERTEXINPUT_STRUCT_NAME} {
    @location(${opts.inputPositionLocation}) position: vec3f,
    @location(${opts.inputNormalLocation}) normal: vec3f,
    @location(${opts.inputUvLocation}) uv: vec2f,
    @builtin(vertex_index) index: u32,
    @builtin(instance_index) instance: u32,
}

struct Transforms {
    world: mat4x4f,
    normal: mat3x3f,
}

struct Camera {
    view: mat4x4f,
    projection: mat4x4f,
    position: vec3f,
}

struct ${WGSL_INTERACTIONDATA_STRUCT_NAME} {
    // mouse coordinatges in NDC space ([-1,1], [-1,1])
    cursor: vec2f,
    // canvas size in pixels
    resolution: vec2f,
    // some phase timer (e.g. time since mouse down, or time since last click, etc.)
    time: f32,
}

${modifierFnInput}

${modifierFnOutput}

@group(${opts.uniformWorldGroupIndex}) @binding(${opts.uniformWorldBindingIndex}) var<uniform> transforms: Transforms;
@group(${opts.uniformViewGroupIndex}) @binding(${opts.uniformViewBindingIndex}) var<uniform> camera: Camera;
@group(${opts.uniformInteractionGroupIndex}) @binding(${opts.uniformInteractionBindingIndex}) var<uniform> interaction: ${WGSL_INTERACTIONDATA_STRUCT_NAME};

@vertex fn main(vertexInput: ${WGSL_VERTEXINPUT_STRUCT_NAME}) -> ${WGSL_VERTEXOUTPUT_STRUCT_NAME} {
    let input = ${modifyInstructionInput};
    let position4 = vec4f(input.position, 1.0);
    let worldTransform = transforms.world;
    let viewProjectionTransform = camera.projection;
    let worldViewProjectionTransform = viewProjectionTransform * worldTransform;
    let worldPosition = (worldTransform * position4).xyz;
    let output = ${WGSL_VERTEXOUTPUT_STRUCT_NAME}(
        worldViewProjectionTransform * position4,
        transforms.normal * input.normal,
        worldPosition,
        input.uv,
        input.index,
        normalize(worldPosition - camera.position),
        input.instance
    );
    return ${modifyInstructionOutput};
}`;
}
